import React, { useState } from 'react';
import { apiService, RealtimeFeed } from '../../services/api';
import { getRealtimeErrorMessage } from '../../features/realtime/errors';
import EngineGallery from '../EngineGallery';
import CustomSelect from '../CustomSelect';
import StagedDialog from '../StagedDialog';
import './RealtimeFeedDialog.css';

const RealtimeFeedDialog: React.FC<{
  projectId: string;
  onClose: () => void;
  onCreated: (feed: RealtimeFeed, credentials: { username?: string; password?: string }) => void;
}> = ({ projectId, onClose, onCreated }) => {
  const [step, setStep] = useState(0);
  const [name, setName] = useState('');
  const [source, setSource] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [engine, setEngine] = useState('');
  const [windowSeconds, setWindowSeconds] = useState('10');
  const [resolution, setResolution] = useState('low');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const sourceReady = Boolean(name.trim() && source.trim());
  const next = () => {
    if (!sourceReady) return;
    if (password && !username.trim()) { setError('Enter a username for this camera password.'); return; }
    setError('');
    setStep(1);
  };
  const submit = async () => {
    if (!sourceReady || !engine || saving) return;
    setSaving(true);
    setError('');
    const credentials = { username: username.trim() || undefined, password: password || undefined };
    try {
      const feed = await apiService.createRealtimeFeed(projectId, {
        name: name.trim(), source_url: source.trim(), engine_id: engine,
        buffer_seconds: Number(windowSeconds) as 10 | 15 | 30 | 60,
        resolution: resolution as 'low' | 'high', ...credentials,
      });
      onCreated(feed, credentials);
    } catch (e) { setError(getRealtimeErrorMessage(e, 'Unable to create feed. Please try again.')); }
    finally { setSaving(false); }
  };
  return <StagedDialog title="Create feed" description={step === 0 ? 'Connect a camera and choose its capture settings.' : 'Select an engine or create one with your fields and alert rubric.'}
    steps={['Source & capture', 'Extraction engine']} step={step} onClose={onClose} busy={saving} className="realtime-create-feed"
    footer={<>
      <button type="button" className="action-button" onClick={() => { if (step === 0) onClose(); else { setStep(0); setError(''); } }} disabled={saving}>{step === 0 ? 'Cancel' : 'Back'}</button>
      {step === 0 ? <button type="button" className="search-button" disabled={!sourceReady} onClick={next}>Next: Extraction engine</button>
        : <button type="button" className="search-button" disabled={saving || !engine} onClick={() => void submit()}>{saving ? 'Creating…' : 'Create & start'}</button>}
    </>}>
    <form hidden={step !== 0} onSubmit={event => { event.preventDefault(); next(); }}>
      <div className="realtime-form-grid">
        <label className="form-group">Feed name<input className="form-input" value={name} onChange={event => setName(event.target.value)} maxLength={120} /></label>
        <label className="form-group">Camera URL<input className="form-input" value={source} onChange={event => setSource(event.target.value)} placeholder="rtsp://camera/stream" maxLength={2048} /></label>
        <label className="form-group">Username (optional)<input className="form-input" value={username} onChange={event => setUsername(event.target.value)} autoComplete="off" /></label>
        <label className="form-group">Password (optional)<input type="password" className="form-input" value={password} onChange={event => setPassword(event.target.value)} autoComplete="new-password" /></label>
        <div className="form-group"><span>Capture window</span><CustomSelect value={windowSeconds} onChange={setWindowSeconds} options={[10, 15, 30, 60].map(seconds => ({ value: String(seconds), label: `${seconds} seconds` }))} ariaLabel="Capture window" /></div>
        <div className="form-group"><span>Model resolution</span><CustomSelect value={resolution} onChange={setResolution} options={[{ value: 'low', label: 'Low · faster' }, { value: 'high', label: 'High · automatic detail' }]} ariaLabel="Model resolution" /></div>
      </div>
      <p className="realtime-help">Capture window and resolution stay fixed for this feed. You can edit its engine later.</p>
    </form>
    <div hidden={step !== 1}>
      <p className="realtime-feed-review"><strong>{name}</strong><span>{windowSeconds}-second captures · {resolution === 'low' ? 'Low resolution' : 'Automatic detail'}</span></p>
      <EngineGallery kind="realtime" selectedPromptId={engine} onSelect={prompt => setEngine(prompt.prompt_id)} manageEditorLinks={false} disabled={saving} />
    </div>
    {error && <p role="alert" className="error-message">{error}</p>}
  </StagedDialog>;
};
export default RealtimeFeedDialog;
